import { useEffect, useState } from 'react';
import { Trash2, Mail, Phone, Calendar } from 'lucide-react';
import { API_URL } from '../config';

const EnquiryManager = () => {
  const [enquiries, setEnquiries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Authorization': token ? `Bearer ${token}` : ''
    };
  };

  const fetchEnquiries = () => {
    fetch(`${API_URL}/api/public/admin/enquiries`, {
      headers: getAuthHeaders()
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch enquiries');
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data)) {
          setEnquiries(data);
        } else {
          console.error('Invalid enquiries data:', data);
          setEnquiries([]);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setEnquiries([]);
        setLoading(false);
      });
  };

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this enquiry?')) {
      try {
        const res = await fetch(`${API_URL}/api/public/admin/enquiries/${id}`, { 
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        const data = await res.json();
        
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Failed to delete');
        }
        fetchEnquiries();
      } catch (error: any) {
        console.error('Delete failed:', error);
        alert(`Failed to delete enquiry: ${error.message}`);
      }
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Enquiries</h1>

      <div className="bg-white rounded-lg shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-sm uppercase tracking-wider">
                <th className="p-4 border-b border-slate-200 font-semibold">Date</th>
                <th className="p-4 border-b border-slate-200 font-semibold">Name</th>
                <th className="p-4 border-b border-slate-200 font-semibold">Contact</th>
                <th className="p-4 border-b border-slate-200 font-semibold">Interest</th>
                <th className="p-4 border-b border-slate-200 font-semibold">Message</th>
                <th className="p-4 border-b border-slate-200 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading enquiries...</td></tr>
              ) : enquiries.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">No enquiries found.</td></tr>
              ) : (
                enquiries.map((enquiry) => (
                  <tr key={enquiry.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 text-sm text-slate-500 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar size={14} className="mr-2" /> {new Date(enquiry.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="p-4 font-medium text-slate-900">{enquiry.name}</td>
                    <td className="p-4 text-sm text-slate-600">
                      <div className="flex items-center mb-1">
                        <Mail size={14} className="mr-2 text-slate-400" /> {enquiry.email}
                      </div>
                      <div className="flex items-center">
                        <Phone size={14} className="mr-2 text-slate-400" /> {enquiry.phone}
                      </div>
                    </td>
                    <td className="p-4 text-sm text-slate-600">{enquiry.interest}</td>
                    <td className="p-4 text-sm text-slate-600 max-w-xs truncate">
                      {enquiry.message}
                    </td>
                    <td className="p-4 text-right">
                      <button 
                        onClick={() => handleDelete(enquiry.id)}
                        className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50 transition-colors" 
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EnquiryManager;
